// ElementBlock — the shared shell around one element, pure.
//
// This is the O(1) shared shell of the editor (project_editor_is_material_
// surface): it is template-AGNOSTIC and renders no element kind itself. It
// receives the per-kind `body` (rendered by the registry) and wraps it in the
// wear every element carries regardless of kind:
//   • a GUTTER of ChangeMarkers — the proposal marker (derived from `proposal`)
//     plus any issue / grounding markers the caller composes;
//   • a ProvenanceTag — who last authored this, and whether anything is pending;
//   • the REVIEW surface — when open, the op-aware ReviewControl anchored below
//     the element (.claude/research/design/03: approve AT the change);
//   • the HANDLE (pen Q76, "one handle, menu on click"): pointing at the block
//     or focus inside it shows one small grip in the margin; a click (or Enter)
//     opens the block menu: Turn into Heading / Subheading / Text, Move up,
//     Move down, Delete. Escape closes it (Radix, via Menu).
//
// The field-vs-block seam (the pluggable-renderer boundary): a FIELD-grain op
// (scalar-edit / list-edit) is content-internal, so the per-kind renderer draws
// its InlineDiff inside `body`; a BLOCK-grain op (add / remove / move) is
// structural, so THIS shell wraps `body` in the block-level InlineDiff. Each
// side decides by matching the op — no flag is threaded. Structural change is
// the shell's job; content change is the renderer's, exactly the own-the-AST
// split.
//
// Pure/props-driven: content + proposal + markers in; every interaction reports
// UP (toggle review, pick an outcome, activate a marker). No edit state here —
// the parent (MaterialDocument, then the app) owns it.

import { exhaustSwitchCase } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Icon, type IconName } from "../../Icon/Icon.js";
import { Menu, type MenuItem } from "../../Menu/Menu.js";
import { ChangeMarker } from "../ChangeMarker/ChangeMarker.js";
import { InlineDiff } from "../InlineDiff/InlineDiff.js";
import { ProvenanceTag } from "../ProvenanceTag/ProvenanceTag.js";
import { ReviewControl } from "../ReviewControl/ReviewControl.js";
import type { TextKind } from "../textKind.js";
import type {
    DiffOp,
    GutterMarker,
    PendingProposal,
    Provenance,
    ReviewOutcome,
} from "../types.js";
import "./ElementBlock.css";

export type ElementBlockProps = {
    // The per-kind rendered content (from the renderer registry).
    readonly body: ReactNode;
    readonly authoredBy: Provenance;
    // The pending agent proposal on this element, if any.
    readonly proposal?: PendingProposal;
    // Extra gutter wear beyond the proposal marker (issues, grounding read-wear).
    readonly markers?: readonly GutterMarker[];
    // Whether the review surface is open (controlled by the parent).
    readonly reviewOpen?: boolean;
    readonly onToggleReview?: () => void;
    readonly onReview?: (outcome: ReviewOutcome) => void;
    readonly onMarkerActivate?: (marker: GutterMarker) => void;
    readonly onJumpToSource?: (anchorIndex: number) => void;
    readonly overTrustCue?: boolean;
    // The block menu's actions. Each is present only when the document is
    // editable AND the move is legal at this position: `onMoveUp` is absent on
    // the first block, `onMoveDown` on the last (the row rests disabled, so the
    // menu never offers an impossible move). `onRemove` deletes the block.
    readonly onMoveUp?: () => void;
    readonly onMoveDown?: () => void;
    readonly onRemove?: () => void;
    // Turn into: what the block is now and the pick (absent: a kind that
    // turns into nothing, or a document that is not editable).
    readonly turnInto?: {
        readonly current: TextKind;
        readonly onTurn: (to: TextKind) => void;
    };
};

// The menu's Turn into rows, in order.
const TEXT_KINDS: readonly TextKind[] = ["heading", "subheading", "text"];

// A block-grain op is wrapped by the shell; a field-grain op is drawn by the
// renderer inside the body. Named so the seam is legible at the call site.
const isBlockOp = (op: DiffOp): boolean =>
    op.type === "add" || op.type === "remove" || op.type === "move";

export const ElementBlock = ({
    body,
    authoredBy,
    proposal,
    markers = [],
    reviewOpen,
    onToggleReview,
    onReview,
    onMarkerActivate,
    onJumpToSource,
    overTrustCue,
    onMoveUp,
    onMoveDown,
    onRemove,
    turnInto,
}: ElementBlockProps) => {
    const { t } = useLingui();
    // Block-grain proposals wrap the body in the shell's InlineDiff; field-grain
    // proposals were already drawn inside `body` by the renderer.
    const content =
        proposal && isBlockOp(proposal.op) ? (
            <InlineDiff op={proposal.op}>{body}</InlineDiff>
        ) : (
            body
        );

    // The handle exists only when the document is editable (at least one
    // action present). Focus-revealed via CSS (:focus-within / hover), so a
    // reading face stays clean but the handle is always keyboard-reachable.
    const hasControls =
        onMoveUp !== undefined ||
        onMoveDown !== undefined ||
        onRemove !== undefined ||
        turnInto !== undefined;
    const kindRow = (kind: TextKind): readonly [IconName, string] => {
        switch (kind) {
            case "heading":
                return ["heading-2", t`Heading`];
            case "subheading":
                return ["heading-3", t`Subheading`];
            case "text":
                // Its own context: body text, not any "Text" (sv
                // "Brödtext").
                return [
                    "paragraph",
                    t({ message: "Text", context: "turn into" }),
                ];
            default:
                return exhaustSwitchCase(kind);
        }
    };
    const menuItems: readonly MenuItem[] = [
        ...(turnInto === undefined
            ? []
            : [
                  { _tag: "label", label: t`Turn into` } satisfies MenuItem,
                  ...TEXT_KINDS.map((kind): MenuItem => {
                      const [icon, label] = kindRow(kind);
                      return {
                          _tag: "item",
                          label,
                          icon: <Icon name={icon} size="sm" />,
                          checked: turnInto.current === kind,
                          onSelect: () => turnInto.onTurn(kind),
                      };
                  }),
                  { _tag: "separator" } satisfies MenuItem,
              ]),
        {
            _tag: "item",
            label: t`Move up`,
            icon: <Icon name="arrow-up" size="sm" />,
            disabled: onMoveUp === undefined,
            onSelect: () => onMoveUp?.(),
        },
        {
            _tag: "item",
            label: t`Move down`,
            icon: <Icon name="arrow-down" size="sm" />,
            disabled: onMoveDown === undefined,
            onSelect: () => onMoveDown?.(),
        },
        ...(onRemove === undefined
            ? []
            : [
                  { _tag: "separator" } satisfies MenuItem,
                  {
                      _tag: "item",
                      label: t`Delete`,
                      icon: <Icon name="trash" size="sm" />,
                      onSelect: onRemove,
                  } satisfies MenuItem,
              ]),
    ];

    return (
        <div
            className={`bilda-element-block${
                proposal ? " bilda-element-block--pending" : ""
            }`}
        >
            <aside className="bilda-element-block__gutter">
                {proposal ? (
                    <ChangeMarker
                        marker={{ type: "proposal" }}
                        expanded={reviewOpen}
                        onActivate={onToggleReview}
                    />
                ) : null}
                {markers.map((marker, i) => (
                    <ChangeMarker
                        // biome-ignore lint/suspicious/noArrayIndexKey: sketch fixture — markers are a small, static, non-reorderable per-element list; real markers will carry stable ids
                        key={`${marker.type}-${i}`}
                        marker={marker}
                        onActivate={() => onMarkerActivate?.(marker)}
                    />
                ))}
                {hasControls ? (
                    <div className="bilda-element-block__controls">
                        <Menu
                            align="start"
                            contentClassName="bilda-element-block__menu"
                            items={menuItems}
                            trigger={
                                <button
                                    type="button"
                                    className="bilda-element-block__handle"
                                    aria-label={t`Block options`}
                                    title={t`Block options`}
                                >
                                    <Icon name="grip" size="sm" />
                                </button>
                            }
                        />
                    </div>
                ) : null}
            </aside>

            <div className="bilda-element-block__main">
                <div className="bilda-element-block__wear">
                    <ProvenanceTag authoredBy={authoredBy} pending={proposal} />
                </div>
                <div
                    className="bilda-element-block__body"
                    // Names this div as the block's CONTENT ONLY — excludes
                    // the gutter, ProvenanceTag and review surface, which
                    // also live inside the outer `[data-element-id]` block
                    // wrapper. MaterialDocument's copy/cut handler reads
                    // this node's `textContent` to test whether a
                    // single-block selection covers the block's ENTIRE
                    // text (as opposed to a partial in-block range) — the
                    // ONLY reason this attribute exists.
                    data-block-body=""
                >
                    {content}
                </div>
                {proposal && reviewOpen ? (
                    <div className="bilda-element-block__review">
                        <ReviewControl
                            proposal={proposal}
                            onReview={(o) => onReview?.(o)}
                            overTrustCue={overTrustCue}
                            onJumpToSource={onJumpToSource}
                        />
                    </div>
                ) : null}
            </div>
        </div>
    );
};
